import React, { useEffect, useRef, useState } from 'react';
import { X, Trash, MapPin, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Per-barn interactive stall placement editor.
 *
 * Producer drags one or more rectangles on the barn's floor-plan image to
 * define where stalls live. On save, the markers are persisted to the barn's
 * `detail_markers` array.
 *
 * Marker semantics (matched in SpotPicker):
 *   - 1 marker → "whole stall area" → spots auto-tile inside the rectangle.
 *   - N markers → each marker is one explicit stall, mapped 1:1 to row/spot.
 */
export const BarnMapEditor = ({
  showId, mapType, barnIdx, barnName, totalSpots,
  detailMapUrl: initialDetailMapUrl, initialMarkers, onClose, onSaved,
}) => {
  // Allow internal updates after upload — initial prop is the parent's snapshot,
  // we keep our own copy that reflects newly-uploaded floor plans within this
  // session.
  const [detailMapUrl, setDetailMapUrl] = useState(initialDetailMapUrl || null);
  const [uploadingFloorPlan, setUploadingFloorPlan] = useState(false);
  const [markers, setMarkers] = useState(initialMarkers || []);
  // Inline editor for a marker's printed number — when set, the corresponding
  // marker's label becomes a number input until blur/Enter commits the change.
  const [editingNumberIdx, setEditingNumberIdx] = useState(null);
  const [drawing, setDrawing] = useState(true);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(null); // 'cells' | 'numbers' | null
  const [detected, setDetected] = useState(null);   // { barns: [...], total_cells, ocr_hits, ocr_total }
  const [applyStart, setApplyStart] = useState(1);
  const [applying, setApplying] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [imgCacheBuster, setImgCacheBuster] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const imgRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fit the entire floor plan inside the canvas viewport on initial load
  // (and on reset / image swap). Producers were complaining that wide or
  // tall maps loaded zoomed-in and required pan/scroll to see the rest.
  const fitToViewport = () => {
    const img = imgRef.current;
    const cont = containerRef.current;
    if (!img || !cont || !img.naturalWidth || !img.naturalHeight) return;
    const cw = cont.clientWidth;
    const ch = cont.clientHeight || 500;
    const fit = Math.min(cw / img.naturalWidth, ch / img.naturalHeight, 1);
    setZoom(Math.max(0.1, fit));
    setPan({ x: 0, y: 0 });
  };

  useEffect(() => {
    setMarkers(initialMarkers || []);
  }, [initialMarkers]);

  // Wheel-to-zoom on the canvas. Bound natively (not via React onWheel) so we
  // can call preventDefault — passive listeners can't, and React 17+ defaults
  // to passive on wheel events.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      setZoom(z => Math.min(Math.max(z + (e.deltaY > 0 ? -0.15 : 0.15), 0.5), 5));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const zoomIn = () => setZoom(z => Math.min(z + 0.3, 5));
  const zoomOut = () => setZoom(z => Math.max(z - 0.3, 0.5));
  const resetView = () => { fitToViewport(); };

  const getPercent = (clientX, clientY) => {
    if (!innerRef.current) return { x: 0, y: 0 };
    const rect = innerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  };

  // Mouse handlers: when drawing → draw rectangle; when dragging marker →
  // move it; otherwise → pan. The marker's own onMouseDown handler starts
  // the drag and stops propagation, so canvas-level down only runs when no
  // marker was clicked.
  const onCanvasMouseDown = (e) => {
    if (draggingIdx !== null) return;
    if (drawing) {
      const p = getPercent(e.clientX, e.clientY);
      setDrawStart(p); setDrawCurrent(p);
    } else {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };
  const onCanvasMouseMove = (e) => {
    if (draggingIdx !== null) {
      moveMarkerDrag(e.clientX, e.clientY);
    } else if (drawing && drawStart) {
      setDrawCurrent(getPercent(e.clientX, e.clientY));
    } else if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
  };
  const onCanvasMouseUp = () => {
    if (draggingIdx !== null) { endMarkerDrag(); return; }
    if (drawing) endDraw();
    setIsPanning(false);
  };

  const startDraw = (clientX, clientY) => {
    if (!drawing) return;
    const p = getPercent(clientX, clientY);
    setDrawStart(p);
    setDrawCurrent(p);
  };
  const moveDraw = (clientX, clientY) => {
    if (!drawing || !drawStart) return;
    setDrawCurrent(getPercent(clientX, clientY));
  };
  const endDraw = () => {
    if (drawing && drawStart && drawCurrent) {
      const x = Math.min(drawStart.x, drawCurrent.x);
      const y = Math.min(drawStart.y, drawCurrent.y);
      const w = Math.abs(drawCurrent.x - drawStart.x);
      const h = Math.abs(drawCurrent.y - drawStart.y);
      if (w > 1 && h > 1) {
        setMarkers(m => [...m, {
          x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2),
        }]);
      }
    }
    setDrawStart(null); setDrawCurrent(null);
  };

  const removeMarker = (idx) => setMarkers(m => m.filter((_, i) => i !== idx));
  const clearAll = () => setMarkers([]);

  // (G) Drag-to-reposition existing markers. When not drawing and not
  // panning, clicking a marker grabs it and dragging moves it.
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dragOrigin, setDragOrigin] = useState(null); // {x,y,mx,my}

  const startMarkerDrag = (idx, clientX, clientY) => {
    const m = markers[idx];
    if (!m) return;
    setDraggingIdx(idx);
    const p = getPercent(clientX, clientY);
    setDragOrigin({ x: m.x, y: m.y, mx: p.x, my: p.y });
  };
  const moveMarkerDrag = (clientX, clientY) => {
    if (draggingIdx === null || !dragOrigin) return;
    const p = getPercent(clientX, clientY);
    const dx = p.x - dragOrigin.mx;
    const dy = p.y - dragOrigin.my;
    setMarkers(prev => prev.map((mk, i) =>
      i === draggingIdx ? {
        ...mk,
        x: Math.max(0, Math.min(100 - mk.w, +(dragOrigin.x + dx).toFixed(2))),
        y: Math.max(0, Math.min(100 - mk.h, +(dragOrigin.y + dy).toFixed(2))),
      } : mk,
    ));
  };
  const endMarkerDrag = () => {
    setDraggingIdx(null);
    setDragOrigin(null);
  };

  // (D) Snap markers to a perfect grid inside the overall bounding box.
  // Useful when AI detection finds most cells but misses a few — grid snap
  // redistributes them into the producer-specified rows × cols.
  const [snapOpen, setSnapOpen] = useState(false);
  const [snapRows, setSnapRows] = useState(2);
  const [snapCols, setSnapCols] = useState(14);
  const [snapStart, setSnapStart] = useState(1);
  const [snapReverseTop, setSnapReverseTop] = useState(false);

  const applySnapToGrid = () => {
    if (!markers.length) { toast.error('No markers to snap — detect or draw first'); return; }
    // Compute current bounding box
    const xMin = Math.min(...markers.map(m => m.x));
    const yMin = Math.min(...markers.map(m => m.y));
    const xMax = Math.max(...markers.map(m => m.x + m.w));
    const yMax = Math.max(...markers.map(m => m.y + m.h));
    const bbW = xMax - xMin;
    const bbH = yMax - yMin;
    if (bbW <= 0 || bbH <= 0) { toast.error('Bounding box invalid'); return; }
    const cellW = bbW / snapCols;
    const cellH = bbH / snapRows;
    const gridMarkers = [];
    let num = parseInt(snapStart, 10) || 1;
    for (let r = 0; r < snapRows; r++) {
      const reverse = r === 0 && snapReverseTop;
      for (let c = 0; c < snapCols; c++) {
        const col = reverse ? (snapCols - 1 - c) : c;
        gridMarkers.push({
          x: +(xMin + col * cellW).toFixed(2),
          y: +(yMin + r * cellH).toFixed(2),
          w: +cellW.toFixed(2),
          h: +cellH.toFixed(2),
          printed_number: num,
        });
        num++;
      }
    }
    setMarkers(gridMarkers);
    setSnapOpen(false);
    toast.success(`Snapped to ${snapRows} × ${snapCols} grid (${gridMarkers.length} stalls)`);
  };

  const handleRotate = async (degrees) => {
    if (rotating) return;
    setRotating(true);
    try {
      const { data } = await api.post(
        `/shows/${showId}/barns/${mapType}/${barnIdx}/rotate-detail-map`,
        { degrees },
      );
      // Cache-bust the image so the rotated version loads
      setImgCacheBuster(Date.now());
      // Reload markers to pick up rotated coords
      const { data: spotMap } = await api.get(`/shows/${showId}/spot-map`);
      const map = mapType === 'stall' ? spotMap.stall_map : spotMap.rv_map;
      const refreshedMarkers = map?.[barnIdx]?.detail_markers || [];
      setMarkers(refreshedMarkers);
      toast.success(data?.message || `Rotated ${degrees}°`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation failed');
    } finally {
      setRotating(false);
    }
  };

  const runDetect = async (mode) => {
    setDetecting(mode);
    setDetected(null);
    try {
      const endpoint = mode === 'cells' ? 'detect-cells' : 'detect-numbers';
      // Dense floor plans can take 2-4 min for GPT-4o OCR. Default axios
      // timeout relies on the ingress limit and produces a bare "Cell
      // detection failed" toast when the proxy hangs up early.
      const timeout = mode === 'cells' ? 180000 : 240000;
      const { data } = await api.post(
        `/shows/${showId}/barns/${mapType}/${barnIdx}/${endpoint}`,
        null,
        { timeout },
      );
      setDetected(data);
      // Preview the detected cells as markers immediately
      const cellMarkers = (data.barns || []).flatMap(b =>
        (b.cells || []).map(c => ({
          x: c.x, y: c.y, w: c.w, h: c.h,
          printed_number: c.printed_number,
        })),
      );
      setMarkers(cellMarkers);
      const hits = data.ocr_hits || 0;
      const tot = data.ocr_total || cellMarkers.length;

      // Auto-apply for 'cells' mode — detected cells are already the actual
      // floor-plan squares (with OCR'd numbers), so there's no value in
      // making the producer click Apply separately. Write rows + markers
      // straight to the chart and close.
      if (mode === 'cells' && cellMarkers.length > 0) {
        try {
          const { data: applyData } = await api.post(
            `/shows/${showId}/barns/${mapType}/${barnIdx}/apply-cells`,
            { barns: data.barns, start_number: parseInt(applyStart, 10) || 1 },
            { timeout: 60000 },
          );
          // Then auto-fit each marker to the actual printed cell pixels so
          // oversized detections collapse to the white cell interior.
          let fitMsg = '';
          try {
            const { data: fitData } = await api.post(
              `/shows/${showId}/barns/${mapType}/${barnIdx}/fit-markers-to-printed-cells`,
              null,
              { timeout: 60000 },
            );
            fitMsg = ` · fit ${fitData.fitted}/${fitData.fitted + fitData.unchanged}`;
          } catch (_fitErr) {
            // Fit step is best-effort; failure shouldn't block the apply.
          }
          toast.success(
            `Detected & applied ${cellMarkers.length} stalls · OCR ${hits}/${tot}${fitMsg}`
              + (applyData?.total_spots_in_barn ? ` · ${applyData.total_spots_in_barn} in barn` : ''),
          );
          // Refresh parent in background; keep modal open so producer can
          // verify the placed markers visually before dismissing.
          onSaved?.(cellMarkers);
          return;
        } catch (applyErr) {
          // Detection worked but auto-apply failed — leave the preview up
          // and surface the manual Apply button so the producer can retry.
          toast.error(
            applyErr?.response?.data?.detail
              || 'Detection succeeded but auto-apply failed — click Apply to retry',
          );
        }
      }

      toast.success(`Detected ${cellMarkers.length} stalls · OCR ${hits}/${tot}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) {
        toast.error('Detection timed out — try cropping the floor plan to a smaller region and re-uploading.');
      } else {
        toast.error(detail || `Failed to ${mode === 'cells' ? 'detect cells' : 'detect numbers'}`);
      }
    } finally {
      setDetecting(null);
    }
  };

  const applyDetected = async () => {
    if (!detected) return;
    setApplying(true);
    try {
      const { data } = await api.post(
        `/shows/${showId}/barns/${mapType}/${barnIdx}/apply-cells`,
        { barns: detected.barns, start_number: parseInt(applyStart, 10) || 1 },
      );
      toast.success(data.message || 'Applied to barn');
      // Markers are now persisted by apply-cells. Notify the parent so it
      // can refresh in the background, but keep the modal open so the
      // producer can visually verify the result before dismissing.
      onSaved?.(markers);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(
        `/shows/${showId}/barns/${mapType}/${barnIdx}/detail-markers`,
        { markers },
      );
      toast.success(`Saved ${markers.length} stall ${markers.length === 1 ? 'area' : 'positions'} for ${barnName}`);
      // Notify parent for refresh; keep the modal open so the producer can
      // verify the saved markers before dismissing.
      onSaved?.(markers);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save markers');
    } finally {
      setSaving(false);
    }
  };

  const previewRect = drawStart && drawCurrent ? {
    x: Math.min(drawStart.x, drawCurrent.x),
    y: Math.min(drawStart.y, drawCurrent.y),
    w: Math.abs(drawCurrent.x - drawStart.x),
    h: Math.abs(drawCurrent.y - drawStart.y),
  } : null;

  // Single big marker = "auto-tile spots inside this rectangle" (riders see
  // a wrapping grid of clickable spots matching the barn's row × spot count).
  // Many small markers = one stall each, mapped in row order.
  const mode = markers.length === 0 ? 'none'
              : markers.length === 1 ? 'auto-tile'
              : 'per-stall';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      data-testid="barn-map-editor-modal"
    >
      {/* Note: backdrop clicks DO NOT close the modal. Producers were getting
          accidentally dumped back to ShowEdit mid-edit via misclicks on the
          dim area. Dismiss only via the explicit X button (line 783) or
          ESC key (handled below). */}
      <div className="bg-[#0F0F0F] border border-[#262626] rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626] sticky top-0 bg-[#0F0F0F] z-10">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-[#FF9500]" />
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              Place stalls on {barnName}
            </h3>
            <span className="text-[10px] text-[#9E9E9E] ml-2">{totalSpots} stalls in this barn</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[#9E9E9E] hover:text-white"
            data-testid="barn-map-editor-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="text-[11px] text-[#9E9E9E] bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-3 py-2 leading-relaxed">
            <span className="text-[#0096FF] font-semibold">Tip:</span> Drag a rectangle on the floor plan to define where stalls live.
            <br />
            <span className="text-[#9E9E9E]">• Draw <span className="text-[#FF9500] font-semibold">one</span> big rectangle around the entire stall block — riders see all {totalSpots} stalls auto-tiled inside it.</span>
            <br />
            <span className="text-[#9E9E9E]">• Draw <span className="text-[#FF9500] font-semibold">{totalSpots}</span> small rectangles, one per stall, for an exact 1:1 layout.</span>
            <br />
            <span className="text-[#9E9E9E]">• Or let the AI do it — see Auto-Detect buttons below.</span>
          </div>

          {/* Auto-detect & rotation actions — same as the show-level chart */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button" onClick={() => runDetect('cells')}
              disabled={!!detecting}
              className="px-3 py-1.5 bg-[#FF9500]/15 border border-[#FF9500]/40 rounded text-[11px] text-[#FF9500] hover:bg-[#FF9500]/25 disabled:opacity-50 font-semibold"
              data-testid="barn-detect-cells-btn"
            >
              {detecting === 'cells' ? 'Scanning…' : '⊞ Detect Individual Cells'}
            </button>
            <button
              type="button" onClick={() => runDetect('numbers')}
              disabled={!!detecting}
              className="px-3 py-1.5 bg-[#AF52DE]/15 border border-[#AF52DE]/40 rounded text-[11px] text-[#AF52DE] hover:bg-[#AF52DE]/25 disabled:opacity-50 font-semibold"
              data-testid="barn-auto-detect-numbers"
            >
              {detecting === 'numbers' ? 'Detecting…' : '🔢 Auto-Detect Numbers'}
            </button>
            <button
              type="button" onClick={() => setSnapOpen(s => !s)}
              disabled={!!detecting}
              title="Snap existing markers to a perfect rows × cols grid inside their bounding box. Fixes missed cells after auto-detect."
              className={`px-3 py-1.5 border rounded text-[11px] font-semibold disabled:opacity-50 ${snapOpen ? 'bg-[#34C759] border-[#34C759] text-black' : 'bg-[#34C759]/15 border-[#34C759]/40 text-[#34C759] hover:bg-[#34C759]/25'}`}
              data-testid="barn-snap-to-grid"
            >
              ▦ Snap to Grid
            </button>
            <div className="flex items-center gap-1 pl-2 ml-1 border-l border-[#262626]">
              <span className="text-[10px] text-[#555] mr-1">Resize all:</span>
              <button
                type="button" onClick={async () => {
                  if (!markers.length) { toast.error('No markers to fit — detect or draw first'); return; }
                  try {
                    const { data } = await api.post(
                      `/shows/${showId}/barns/${mapType}/${barnIdx}/fit-markers-to-printed-cells`,
                      null,
                      { timeout: 60000 },
                    );
                    setMarkers(data.markers || []);
                    toast.success(data.message || 'Fit markers to printed cells');
                    onSaved?.(data.markers || []);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Fit to printed cells failed');
                  }
                }}
                disabled={markers.length === 0}
                title="Look INSIDE each marker's bbox for the printed cell (white interior) and shrink the marker to its exact pixel size. Fixes oversized markers that overlap multiple stalls."
                className="px-2 py-1 bg-[#34C759]/15 border border-[#34C759]/40 rounded text-[11px] text-[#34C759] hover:bg-[#34C759]/25 disabled:opacity-50 font-semibold"
                data-testid="barn-fit-to-cells"
              >
                ⊟ Fit to Printed Cells
              </button>
              <button
                type="button" onClick={async () => {
                  if (!markers.length) { toast.error('No markers to snap — detect or draw first'); return; }
                  try {
                    const { data } = await api.post(
                      `/shows/${showId}/barns/${mapType}/${barnIdx}/snap-markers-to-cells`,
                      { markers },
                      { timeout: 240000 },
                    );
                    setMarkers(data.markers || []);
                    toast.success(data.message || `Snapped ${data.snapped_to_cell} markers`);
                    // Snap persists rows + markers atomically — refresh the parent
                    // in the background but keep the modal open so the producer
                    // can verify the OCR'd numbers + positions before closing.
                    onSaved?.(data.markers || []);
                  } catch (e) {
                    if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) {
                      toast.error('Snap timed out (OCR can take 2-4 min on dense floor plans). Try cropping the floor plan smaller.');
                    } else {
                      toast.error(e?.response?.data?.detail || 'Snap to squares failed');
                    }
                  }
                }}
                disabled={markers.length === 0}
                title="Detect the actual stall squares + read each square's printed number (OCR), then snap each marker to its underlying square so size, position, and stall number all match the floor plan."
                className="px-2 py-1 bg-[#0096FF]/15 border border-[#0096FF]/40 rounded text-[11px] text-[#0096FF] hover:bg-[#0096FF]/25 disabled:opacity-50 font-semibold"
                data-testid="barn-snap-to-cells"
              >
                ⊡ Snap to Squares
              </button>
              <button
                type="button" onClick={async () => {
                  try {
                    const { data } = await api.post(
                      `/shows/${showId}/barns/${mapType}/${barnIdx}/scale-markers`,
                      { factor: 0.8 },
                    );
                    setMarkers(data.markers || []);
                    toast.success(`Shrunk ${(data.markers || []).length} markers to 80%`);
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Scale failed'); }
                }}
                disabled={markers.length === 0}
                title="Shrink all markers to 80% of current size (centered). Use when auto-detected markers look too big over the printed stalls."
                className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                data-testid="barn-shrink-markers"
              >
                Shrink 80%
              </button>
              <button
                type="button" onClick={async () => {
                  try {
                    const { data } = await api.post(
                      `/shows/${showId}/barns/${mapType}/${barnIdx}/scale-markers`,
                      { factor: 1.2 },
                    );
                    setMarkers(data.markers || []);
                    toast.success(`Grew ${(data.markers || []).length} markers to 120%`);
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Scale failed'); }
                }}
                disabled={markers.length === 0}
                title="Grow all markers to 120% of current size (centered)."
                className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                data-testid="barn-grow-markers"
              >
                Grow 120%
              </button>
            </div>
            <div className="flex items-center gap-1 pl-2 ml-1 border-l border-[#262626]">
              <span className="text-[10px] text-[#555] mr-1">Rotate:</span>
              <button
                type="button" onClick={() => handleRotate(270)}
                disabled={rotating}
                className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                title="Rotate 90° counter-clockwise"
                data-testid="barn-rotate-ccw"
              >
                ↺ 90°
              </button>
              <button
                type="button" onClick={() => handleRotate(90)}
                disabled={rotating}
                className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                title="Rotate 90° clockwise"
                data-testid="barn-rotate-cw"
              >
                ↻ 90°
              </button>
              <button
                type="button" onClick={() => handleRotate(180)}
                disabled={rotating}
                className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                title="Flip 180°"
                data-testid="barn-rotate-180"
              >
                180°
              </button>
            </div>
            {detected && (
              <div className="flex items-center gap-2 ml-auto bg-[#0A0A0A] border border-[#34C759]/40 rounded px-2 py-1">
                <span className="text-[10px] text-[#9E9E9E]">Found <span className="text-[#34C759] font-bold">{detected.total_cells}</span> · OCR {detected.ocr_hits}/{detected.ocr_total}</span>
                <span className="text-[10px] text-[#9E9E9E]">Start #</span>
                <input
                  type="number"
                  min="1"
                  value={applyStart}
                  onChange={(e) => setApplyStart(e.target.value)}
                  className="w-14 px-1.5 py-0.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                  data-testid="barn-apply-start"
                />
                <button
                  type="button" onClick={applyDetected}
                  disabled={applying}
                  className="px-2 py-1 bg-[#34C759] rounded text-[10px] text-black font-bold hover:bg-[#34C759]/80 disabled:opacity-50"
                  data-testid="barn-apply-detected"
                >
                  {applying ? 'Applying…' : 'Apply'}
                </button>
              </div>
            )}
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-1 flex-wrap" data-testid="barn-zoom-controls">
            <span className="text-[10px] text-[#555] mr-1">Zoom:</span>
            <button
              type="button" onClick={zoomOut}
              className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-sm font-bold"
              data-testid="barn-zoom-out"
            >−</button>
            <span className="text-[10px] text-[#555] w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button
              type="button" onClick={zoomIn}
              className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-sm font-bold"
              data-testid="barn-zoom-in"
            >+</button>
            <button
              type="button" onClick={resetView}
              className="px-2 py-1 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white"
              data-testid="barn-zoom-reset"
            >Reset</button>
            <span className="text-[10px] text-[#555] ml-2">
              {drawing ? 'Draw mode · click + drag' : (draggingIdx !== null ? 'Dragging marker…' : 'Pan mode · drag to move, scroll to zoom · click any marker to drag it')}
            </span>
          </div>

          {/* Snap-to-grid panel */}
          {snapOpen && (
            <div className="p-3 bg-[#0D1A0D] border border-[#34C759]/40 rounded-lg" data-testid="barn-snap-panel">
              <p className="text-[11px] text-[#9E9E9E] mb-2">
                Redistribute the {markers.length} existing markers into a perfect grid inside their bounding box. Great after Auto-Detect missed a few cells.
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <label className="text-[11px] text-[#9E9E9E]">Rows
                  <input type="number" min="1" max="20" value={snapRows}
                    onChange={(e) => setSnapRows(parseInt(e.target.value, 10) || 1)}
                    className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                    data-testid="barn-snap-rows" />
                </label>
                <label className="text-[11px] text-[#9E9E9E]">× Cols
                  <input type="number" min="1" max="100" value={snapCols}
                    onChange={(e) => setSnapCols(parseInt(e.target.value, 10) || 1)}
                    className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                    data-testid="barn-snap-cols" />
                </label>
                <label className="text-[11px] text-[#9E9E9E]">Start #
                  <input type="number" min="1" value={snapStart}
                    onChange={(e) => setSnapStart(parseInt(e.target.value, 10) || 1)}
                    className="w-16 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                    data-testid="barn-snap-start" />
                </label>
                <label className="text-[10px] text-[#9E9E9E] flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={snapReverseTop}
                    onChange={(e) => setSnapReverseTop(e.target.checked)}
                    data-testid="barn-snap-reverse-top" />
                  <span>Reverse first row</span>
                </label>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={applySnapToGrid}
                  className="px-3 py-1 bg-[#34C759] rounded text-[11px] text-black font-bold hover:bg-[#34C759]/80"
                  data-testid="barn-snap-apply">
                  ✓ Snap to {snapRows}×{snapCols} grid
                </button>
                <button type="button" onClick={() => setSnapOpen(false)}
                  className="px-3 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white"
                  data-testid="barn-snap-cancel">
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div
            ref={containerRef}
            className={`relative w-full select-none overflow-hidden rounded-lg border bg-[#0A0A0A] ${drawing ? 'border-[#FF9500] cursor-crosshair' : 'border-[#262626] cursor-grab'}`}
            style={{ maxHeight: '500px', touchAction: 'none' }}
            onMouseDown={detailMapUrl ? onCanvasMouseDown : undefined}
            onMouseMove={detailMapUrl ? onCanvasMouseMove : undefined}
            onMouseUp={detailMapUrl ? onCanvasMouseUp : undefined}
            onMouseLeave={detailMapUrl ? onCanvasMouseUp : undefined}
            onTouchStart={(e) => { if (detailMapUrl && e.touches[0]) startDraw(e.touches[0].clientX, e.touches[0].clientY); }}
            onTouchMove={(e) => { if (detailMapUrl && e.touches[0]) { e.preventDefault(); moveDraw(e.touches[0].clientX, e.touches[0].clientY); } }}
            onTouchEnd={detailMapUrl ? endDraw : undefined}
            data-testid="barn-map-editor-canvas"
          >
            {!detailMapUrl ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="barn-map-editor-no-image">
                <UploadSimple size={42} className="text-[#FF9500] mb-3" />
                <h4 className="text-white font-bold text-base mb-1">Upload {barnName} floor plan</h4>
                <p className="text-[11px] text-[#9E9E9E] mb-4 max-w-sm">
                  JPG, PNG, WebP, or PDF. After uploading, draw rectangles or use Auto-Detect Cells / Numbers to place stalls.
                </p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9500] rounded text-white text-xs font-bold cursor-pointer hover:bg-[#FF9500]/80 transition-colors">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    disabled={uploadingFloorPlan}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingFloorPlan(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const { data } = await api.post(
                          `/shows/${showId}/barns/${encodeURIComponent(mapType)}/${barnIdx}/detail-map`,
                          fd,
                          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 },
                        );
                        setDetailMapUrl(data.detail_map_url);
                        setImgCacheBuster(Date.now());
                        toast.success('Floor plan uploaded');
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Upload failed');
                      } finally {
                        setUploadingFloorPlan(false);
                        e.target.value = '';
                      }
                    }}
                    className="hidden"
                    data-testid="barn-map-editor-upload-input"
                  />
                  <UploadSimple size={14} />
                  {uploadingFloorPlan ? 'Uploading…' : 'Choose Floor Plan'}
                </label>
              </div>
            ) : (
            <div
              ref={innerRef}
              className="relative"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                transition: isPanning || drawStart ? 'none' : 'transform 0.15s ease-out',
                // CRITICAL: inline-block shrinks the wrapper to the image's
                // natural pixel dimensions. Without this, the wrapper expands
                // to fill the parent (block default), and marker percentage
                // positions get computed against the wrong width — they
                // drift off the floor plan on wide desktop viewports.
                display: 'inline-block',
                lineHeight: 0,
              }}
            >
              <img
                ref={imgRef}
                src={`${backendUrl}${detailMapUrl}${imgCacheBuster ? `&_=${imgCacheBuster}` : ''}`}
                alt={`${barnName} floor plan`}
                style={{ pointerEvents: 'none', display: 'block' }}
                draggable={false}
                data-testid="barn-map-editor-img"
                onLoad={fitToViewport}
              />
              {markers.map((m, mi) => (
                <div
                  key={mi}
                  className={`absolute border-2 rounded-sm ${draggingIdx === mi ? 'border-[#FF9500] bg-[#FF9500]/30 cursor-grabbing' : 'border-[#34C759] bg-[#34C759]/20 cursor-grab hover:border-[#34C759]'}`}
                  style={{ left: `${m.x}%`, top: `${m.y}%`, width: `${m.w}%`, height: `${m.h}%` }}
                  onMouseDown={(e) => { e.stopPropagation(); if (!drawing) startMarkerDrag(mi, e.clientX, e.clientY); }}
                  onTouchStart={(e) => { e.stopPropagation(); if (!drawing && e.touches[0]) startMarkerDrag(mi, e.touches[0].clientX, e.touches[0].clientY); }}
                  data-testid={`barn-marker-${mi}`}
                  title={drawing ? 'Switch off Draw mode to drag' : 'Drag to reposition'}
                >
                  {editingNumberIdx === mi ? (
                    <input
                      type="number"
                      min="1"
                      autoFocus
                      defaultValue={m.printed_number != null ? m.printed_number : mi + 1}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setMarkers(prev => prev.map((mm, idx) => idx === mi
                          ? { ...mm, printed_number: Number.isFinite(v) && v > 0 ? v : null }
                          : mm));
                        setEditingNumberIdx(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') { setEditingNumberIdx(null); }
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      className="absolute -top-5 left-0 w-14 text-[10px] font-bold px-1 py-0.5 rounded bg-[#0A0A0A] border border-[#FF9500] text-[#FF9500] focus:outline-none"
                      data-testid={`barn-marker-number-input-${mi}`}
                    />
                  ) : (
                    <span
                      className="absolute -top-4 left-0 text-[9px] font-bold px-1 py-0.5 rounded bg-[#0A0A0A]/95 text-[#34C759] whitespace-nowrap cursor-text hover:bg-[#0A0A0A] hover:text-[#FF9500] hover:underline"
                      onClick={(e) => { e.stopPropagation(); setEditingNumberIdx(mi); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      title="Click to rename this stall"
                      data-testid={`barn-marker-label-${mi}`}
                    >
                      {markers.length === 1 ? `${totalSpots} stalls auto-tile here` :
                       m.printed_number != null ? `#${m.printed_number}` :
                       `Stall ${mi + 1}`}
                    </span>
                  )}
                  <button
                    type="button" onClick={(e) => { e.stopPropagation(); removeMarker(mi); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#FF3B30] text-white flex items-center justify-center hover:bg-[#FF3B30]/80"
                    data-testid={`barn-marker-remove-${mi}`}
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
              {previewRect && (
                <div
                  className="absolute border-2 border-dashed border-[#FF9500] bg-[#FF9500]/15 rounded-sm pointer-events-none"
                  style={{ left: `${previewRect.x}%`, top: `${previewRect.y}%`, width: `${previewRect.w}%`, height: `${previewRect.h}%` }}
                />
              )}
              {drawing && markers.length === 0 && !previewRect && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#FF9500] text-black text-xs font-bold px-3 py-1 rounded-full pointer-events-none z-10">
                  Click and drag to draw
                </div>
              )}
            </div>
            )}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-[#9E9E9E]">
                {markers.length} {markers.length === 1 ? 'rectangle' : 'rectangles'} drawn
                {mode === 'auto-tile' && <span className="ml-2 text-[#34C759]">· Auto-tile mode</span>}
                {mode === 'per-stall' && <span className="ml-2 text-[#0096FF]">· Per-stall mode</span>}
              </span>
              {mode === 'per-stall' && markers.length !== totalSpots && (
                <span className="text-[10px] text-[#FF9500]">
                  ({totalSpots - markers.length > 0 ? `need ${totalSpots - markers.length} more` : `${markers.length - totalSpots} extra`})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button" onClick={() => setDrawing(d => !d)}
                className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-colors ${drawing ? 'bg-[#FF9500] text-black' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white'}`}
                data-testid="barn-map-editor-toggle-draw"
              >
                {drawing ? 'Drawing…' : 'Start drawing'}
              </button>
              {markers.length > 0 && (
                <button
                  type="button" onClick={clearAll}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#FF3B30] hover:border-[#FF3B30]/50 flex items-center gap-1"
                  data-testid="barn-map-editor-clear"
                >
                  <Trash size={11} /> Clear all
                </button>
              )}
              <button
                type="button" onClick={onClose}
                className="px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white"
                data-testid="barn-map-editor-cancel"
              >
                Cancel
              </button>
              <button
                type="button" onClick={save}
                disabled={saving}
                className="px-4 py-1.5 bg-[#0096FF] hover:bg-[#00D4FF] text-white rounded text-[11px] font-semibold disabled:opacity-50"
                data-testid="barn-map-editor-save"
              >
                {saving ? 'Saving…' : 'Save stall positions'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarnMapEditor;
